<template>
  <div class="p-m-3 sp-page">
    <MockBanner>
      The finder results on this page were produced by the prototype on a copy of 16,302 real enquiries (2024–2026).
    </MockBanner>
    <h2>Duplicate finder: how what is typed changes what it can catch</h2>
    <p class="sp-sub">
      When someone starts a new enquiry, they pick the Site first. As they type, the finder shows up to five Sites that already exist, so they can pick
      one instead of creating a copy. Below are five real cases, run through the prototype.
    </p>

    <div class="p-card p-p-4">
      <div class="sp-row" style="gap: 10px;">
        <span class="sp-chip live">Type postcode</span><i class="pi pi-arrow-right"></i>
        <span class="sp-chip live">Type site name</span><i class="pi pi-arrow-right"></i>
        <span class="sp-chip live">Finder shows up to 5 existing Sites</span><i class="pi pi-arrow-right"></i>
        <span class="sp-chip live">Pick one, or create a new Site</span>
      </div>
      <p class="p-mb-0">
        The finder only suggests. The sales person always decides, so a wrong suggestion costs a click, while a missed one creates a duplicate Site.
        No AI runs when an enquiry is entered: the finder uses word and spelling matching plus a small text-similarity model on MBP's own server.
      </p>
    </div>

    <div class="sp-row p-mt-3">
      <div class="sp-kpi" v-for="f in facts" :key="f.v">
        <div class="value">{{ f.v }}</div>
        <div class="hint">{{ f.t }}</div>
      </div>
    </div>

    <div class="p-card p-p-4 p-mt-3">
      <h4 style="margin-top: 0;">Five scenarios</h4>
      <div class="fs-tabs">
        <button v-for="(s, i) in scenarios" :key="s.tab" type="button" :class="{ on: i === cur }" @click="pick(i)">{{ s.tab }}</button>
      </div>

      <h3 class="p-mt-3 p-mb-1">{{ sc.title }}</h3>
      <p class="sp-muted p-mt-0">{{ sc.intro }}</p>
      <div v-if="sc.states.length > 1" class="fs-toggle">
        <button v-for="(x, i) in sc.states" :key="x.label" type="button" :class="{ on: i === state }" @click="state = i">{{ x.label }}</button>
      </div>

      <div class="fs-screen">
        <div class="fs-form">
          <label>Postcode<span class="fs-val" :class="{ empty: !st.pc }">{{ st.pc || 'not typed' }}</span></label>
          <label>Site name<span class="fs-val">{{ st.name }}</span></label>
        </div>
        <div>
          <div class="fs-cap">Existing Sites the finder shows</div>
          <div v-for="(r, i) in st.rows" :key="i" class="fs-row" :class="r.kind">
            <span class="fs-n">{{ i + 1 }}</span>
            <div>
              <div class="fs-nm">{{ r.name }}</div>
              <div class="sp-muted" style="font-size: 12px;">
                {{ r.town }} · <strong v-if="r.pc">{{ r.pc }}</strong><span v-else>no postcode</span> · {{ r.n }} {{ r.n === 1 ? 'enquiry' : 'enquiries' }}
              </div>
            </div>
            <span v-if="r.kind === 'same'" class="sp-chip Won">Same site: pick this</span>
            <span v-else-if="r.kind === 'dup'" class="sp-chip Won">{{ r.tag }}</span>
            <span v-else-if="r.kind === 'alike'" class="sp-chip Medium">{{ r.tag }}</span>
            <span v-else></span>
          </div>
          <div v-if="st.missing" class="sp-note warn"><i class="pi pi-exclamation-triangle"></i><span>{{ st.missing }}</span></div>
        </div>
      </div>

      <div class="sp-note info p-mt-3"><i class="pi pi-info-circle"></i><span><strong>What this shows.</strong> {{ sc.lesson }}</span></div>
      <div v-if="sc.seedNote" class="sp-note grey"><i class="pi pi-database"></i><span>{{ sc.seedNote }}</span></div>
      <p class="sp-muted" style="font-size: 13px;" v-html="sc.stat"></p>
    </div>

    <div class="p-card p-p-4 p-mt-3">
      <h4 style="margin-top: 0;">Across all tested entries</h4>
      <p class="sp-muted p-mt-0">
        130 enquiries where Claude Opus confirmed the same site already existed and the entry had a postcode, typed in six ways. "Found" means the right
        Site was among the five shown. The last column uses 47 look-alike pairs that Opus confirmed are different sites: how often the look-alike came first.
      </p>
      <table class="sp-table">
        <thead><tr><th>What the sales person has typed</th><th>Right Site found</th><th>Right Site first</th><th>Look-alike shown first</th></tr></thead>
        <tbody>
          <tr v-for="r in stats" :key="r[0]">
            <td>{{ r[0] }}</td>
            <td class="fs-num"><span class="fs-bar" :style="{ width: Math.round((r[1] - 60) * 2) + 'px' }"></span>{{ r[1].toFixed(1) }}%</td>
            <td class="fs-num">{{ r[2].toFixed(1) }}%</td>
            <td class="fs-num">{{ r[3].toFixed(1) }}%</td>
          </tr>
        </tbody>
      </table>
      <p class="sp-muted" style="font-size: 13px;">
        The postcode on its own finds less, because 57% of the Sites built from old enquiries have no postcode to match. The name still does most of the
        work today. The postcode helps most with chains and common names, and gets more useful as every new Site is saved with one.
      </p>
    </div>

    <div class="p-card p-p-4 p-mt-3">
      <h4 style="margin-top: 0;">About the existing Sites in these examples</h4>
      <ul class="p-mb-0">
        <li>The 8,764 Sites the finder searches were built automatically from the old enquiries: each description was read once, then grouped by rules.</li>
        <li>That grouping agreed with Claude Opus on <strong>83 of 100</strong> new pairs checked blind, and on <strong>93%</strong> of the pairs where Opus itself was sure.
          Most disagreements are vague names that even Opus was unsure about.</li>
        <li>So some old Sites are wrongly merged or split. Example: the Premier Inn Warrington Site in scenario 3 also holds one Premier Inn Haydock Park enquiry.
          Scenario 4 shows the opposite: one school held as two Sites.</li>
        <li>That is why the plan has a <strong>one-off review of the old data</strong> after seeding (the Duplicate Projects page), before MBP rely on the Sites.
          New Sites entered through the form do not have this problem, because a person picks or creates each one.</li>
      </ul>
    </div>

    <div class="p-card p-p-4 p-mt-3">
      <h4 style="margin-top: 0;">Decision for MBP: what should the form ask for first?</h4>
      <div class="sp-row" style="align-items: stretch;">
        <div v-for="o in options" :key="o.h" class="fs-opt" :class="{ rec: o.rec }">
          <span v-if="o.rec" class="sp-chip live">Recommended</span>
          <h4 class="p-my-2">{{ o.h }}</h4>
          <ul class="p-m-0"><li v-for="l in o.l" :key="l">{{ l }}</li></ul>
        </div>
      </div>
      <h4>Rules that go with any option</h4>
      <ol class="p-mb-0">
        <li>"-", ".", "TBC" and similar are not accepted as a postcode. Blank means "not known yet". Today 54% of enquiries have "-".</li>
        <li>Town means the town of the site, not the customer's office. Today about 1 in 11 enquiries has a town that is not the site's, often the customer's.</li>
        <li>Phase or block is entered as the Project, separately from the Site name. It is named on only 19% of enquiries today.</li>
        <li>The finder never merges on its own. It shows the town and postcode of each suggestion so the sales person can tell look-alikes apart.</li>
      </ol>
    </div>

    <p class="sp-muted p-mt-3" style="font-size: 12px;">
      How this was tested: for 388 pairs of enquiries whose answer Claude Opus had confirmed, the later enquiry was typed in as a new entry, with the earlier
      one already in the list of Sites, and the finder's top five was checked. Prototype only, run on a copy of the data. Nothing has been changed in the CRM.
    </p>
  </div>
</template>

<script>
import MockBanner from './MockBanner.vue'
import './sitesProjects.css'

// Results from the prototype (sites_proto/demo_export.py, 11/10/2026): the finder's top five for each example, "rules + vectors" engine.
const row = (name, town, pc, n, kind, tag) => ({ name, town, pc, n, kind, tag })

const SCENARIOS = [
  {
    tab: '1. Full details', title: 'Everything typed in',
    intro: 'A repeat enquiry for The MIN, the former Royal Mineral Water Hospital in Bath. The sales person types the full postcode and the name.',
    states: [{ label: 'Full postcode + name', pc: 'BA1 1RL', name: 'The MIN, Royal Mineral Water Hospital', rows: [
      row('THE MIN - FORMER ROYAL MINERAL WATER HOSPITAL', 'Bath', 'BA1 1RL', 3, 'same'),
      row('ROYAL HOSPITAL', 'Wolverhampton', 'WV2 1BX', 4),
      row('PRINCESS ROYAL HOSPITAL', 'Telford', '', 1),
      row('ROYAL BERKSHIRE HOSPITAL', 'Reading', 'RG1 5AN', 1),
      row('SALFORD ROYAL HOSPITAL', 'Salford', 'M6 8HD', 1)] }],
    lesson: 'With full details the right Site comes first, well clear of the other "Royal ... Hospital" sites.',
    stat: 'Across 130 tested entries: <strong>97%</strong> found, <strong>93%</strong> first.'
  },
  {
    tab: '2. New development', title: 'A new location with only the postcode area',
    intro: "Brunel Quarter in Chepstow. On a new development the full postcode often doesn't exist yet, so the sales person types just the area: NP16.",
    states: [{ label: 'Area only + name', pc: 'NP16', name: 'Brunel Qtr', rows: [
      row('BRUNEL QTR', 'Chepstow', 'NP16 5PB', 3, 'same'),
      row('BRUNEL QTR POBL HA APARTMENTS', 'Cardiff', '', 1, 'alike', 'Other town'),
      row('BRUNEL QUARTER', 'Neath', '', 1, 'alike', 'Other town'),
      row('BRUNEL STREET, SILVERTOWN WAY', 'London E', 'E16 1EA', 1),
      row('BRUNEL WALK', 'London N', 'N15 5HQ', 4)] }],
    lesson: 'The postcode area is enough to put the right Site first. Two other Brunel Quarters show up, but their towns make it clear they are elsewhere.',
    stat: 'Across 130 tested entries, area only: <strong>97%</strong> found, <strong>92%</strong> first: the same as a full postcode.'
  },
  {
    tab: '3. Chain hotel', title: 'A common name: Premier Inn',
    intro: 'The CRM already holds 63 Premier Inn sites. This is a repeat enquiry for the one in Warrington.',
    states: [
      { label: 'Name only', pc: '', name: 'Premier Inn',
        missing: 'The Warrington Premier Inn is not in the five shown. It comes 17th of the many Premier Inns.', rows: [
          row('PREMIER INN', 'Salisbury', '', 1, 'alike', 'Other hotel'),
          row('PREMIER INN MANCHESTER AIRPORT', 'Manchester', 'M4 4HT', 4, 'alike', 'Other hotel'),
          row('PREMIER INN', 'Windsor', '', 2, 'alike', 'Other hotel'),
          row('PREMIER INN', 'Birmingham', '', 1, 'alike', 'Other hotel'),
          row('PREMIER INN', 'Barry', 'AL10 0DA', 7, 'alike', 'Other hotel')] },
      { label: 'Name + area WA2', pc: 'WA2', name: 'Premier Inn', rows: [
        row('PREMIER INN', 'Warrington', 'WA2 8RN', 9, 'same'),
        row('PREMIER INN', 'Salisbury', '', 1),
        row('PREMIER INN', 'Barry', 'AL10 0DA', 7),
        row('PREMIER INN MANCHESTER AIRPORT', 'Manchester', 'M4 4HT', 4),
        row('PREMIER INN', 'Rhyl', '', 1)] }],
    lesson: 'For chains, schools and other common names, the postcode is what finds the right Site. Without it, the next Warrington enquiry would start a new Site.',
    seedNote: 'About the old data: of the 9 enquiries on this Site, one is actually for Premier Inn Haydock Park (WA3 3JD), wrongly grouped here when the old enquiries were sorted into Sites. The one-off review after seeding is where this gets fixed. It does not affect the finder.',
    stat: 'Name only, across 130 entries: <strong>97%</strong> found, but only <strong>89%</strong> first. Most of the misses are common names.'
  },
  {
    tab: '4. Word left out', title: 'The sales person uses shorthand',
    intro: 'South Axholme Academy, typed as "South Academy" with the area DN9.',
    states: [{ label: 'Shorthand + area', pc: 'DN9', name: 'South Academy', rows: [
      row('ZOUCH ACADEMY', 'Tidworth', 'SP9 7JF', 1),
      row('SOUTH AXHOLME ACADEMY', 'Rotherham', '', 1, 'dup', 'Same school, old record'),
      row('SOUTH AXHOLME ACADEMY', 'Doncaster', 'DN9 1BY', 3, 'same'),
      row('STAR ACADEMY', 'Radcliffe', '', 1),
      row('OULTON ACADEMY', 'Leeds', 'LS26 8EX', 1)] }],
    lesson: 'The right Site is still shown, in 3rd place. Word matching alone would put it 6th, off the list; the small text-similarity model brings it back.',
    seedNote: 'About the old data: row 2 is the same school, entered without a postcode and with a different town, so it became a separate Site. That is the kind of old duplicate the one-off review merges.',
    stat: 'Shorthand across 130 entries: <strong>92%</strong> found (85% with word matching alone). This is the weakest case, so the full name is worth asking for.'
  },
  {
    tab: '5. Look-alike', title: 'Similar name, different place',
    intro: 'Leeside Road in London N17. MBP has also quoted for Leyes Road in London E16, a different site.',
    states: [{ label: 'Full postcode + name', pc: 'N17 0QJ', name: 'Leeside Road', rows: [
      row('LEESIDE ROAD', 'London N', 'N17 0QJ', 5, 'same'),
      row('LEYES ROAD', 'London E', 'E16 3RG', 4, 'alike', 'Different site'),
      row('MILE END ROAD', 'Nottingham', 'NG4 2DW', 1),
      row('CLARE ROAD', 'Sutton', 'NG17 5BE', 1),
      row('DIBDEN ROAD', 'Norwich', 'NR3 4SN', 1)] }],
    lesson: 'The finder shows both because the names look alike. The postcodes, N17 against E16, make the choice obvious. This is why the finder suggests and never merges by itself.',
    stat: 'Across 47 confirmed look-alike pairs, the look-alike came first <strong>17–19%</strong> of the time with a postcode, and <strong>26%</strong> with name and town only.'
  }
]

export default {
  name: 'FinderScenarios',
  components: { MockBanner },
  data() {
    return {
      cur: 0,
      state: 0,
      scenarios: SCENARIOS,
      facts: [
        { v: '33%', t: 'of enquiries (2024–2026) have a full postcode' },
        { v: '54%', t: 'have just "-" in the postcode box, because the field is required today' },
        { v: '5%', t: 'have only the postcode area, such as NP16' },
        { v: '43%', t: 'of the 8,764 Sites built from old enquiries have a full postcode' }
      ],
      stats: [
        ['Full postcode + name', 96.9, 93.1, 19.1],
        ['Postcode area + name', 96.9, 92.3, 17.0],
        ['Name only', 96.9, 89.2, 14.9],
        ['Name misspelt + postcode area', 97.7, 89.2, 17.0],
        ['Name with a word left out + postcode area', 91.5, 76.9, 19.1],
        ['Full postcode only, before the name', 84.6, 80.8, 14.9]
      ],
      options: [
        { h: 'A. Full postcode required', l: [
          'Finds the most: 97% found, 93% first.',
          'Blocks new developments that have no full postcode yet.',
          'Risk: people type a made-up or office postcode to get past the box, the same problem as today\'s "-".'] },
        { h: 'B. Postcode first, full or area', rec: true, l: [
          'The full postcode, or just the area (NP16) when the location is new.',
          'Finds as well as a full postcode: 97% found, 92% first.',
          'Sites saved with only an area are listed for the full postcode later. When it is added, the finder checks again for a duplicate Site.'] },
        { h: 'C. Postcode optional', l: [
          'The name alone still finds 97%, but only 89% first.',
          'Chains and common names get lost: "Premier Inn" alone does not find the Warrington hotel.',
          "Today's pattern would carry on: over half of Sites without a postcode."] }
      ]
    }
  },
  computed: {
    sc() { return this.scenarios[this.cur] },
    st() { return this.sc.states[this.state] }
  },
  methods: {
    pick(i) { this.cur = i; this.state = 0 }
  }
}
</script>

<style scoped>
.fs-tabs, .fs-toggle { display: flex; flex-wrap: wrap; gap: 6px; }
.fs-tabs button, .fs-toggle button { font-size: 13px; background: #fff; border: 1px solid #ccc; border-radius: 16px; padding: 5px 12px; cursor: pointer; }
.fs-tabs button.on { background: #1976d2; border-color: #1976d2; color: #fff; }
.fs-toggle { margin-bottom: 10px; }
.fs-toggle button.on { background: #e3f2fd; border-color: #1976d2; color: #0d47a1; font-weight: 600; }
.fs-screen { display: grid; grid-template-columns: minmax(0, 280px) minmax(0, 1fr); gap: 18px; align-items: start; }
@media (max-width: 760px) { .fs-screen { grid-template-columns: minmax(0, 1fr); } }
.fs-form { display: grid; gap: 10px; background: #f5f5f5; border-radius: 6px; padding: 14px; }
.fs-form label { display: grid; gap: 4px; font-size: 12px; font-weight: 600; color: #666; }
.fs-val { font-family: Menlo, Consolas, monospace; font-size: 14px; color: #222; background: #fff; border: 1px solid #ddd; border-radius: 4px; padding: 7px 9px; min-height: 36px; font-weight: 500; }
.fs-val.empty { color: #999; font-style: italic; font-family: inherit; font-weight: 400; }
.fs-cap { font-size: 12px; font-weight: 600; color: #666; margin-bottom: 6px; }
.fs-row { display: grid; grid-template-columns: 22px minmax(0, 1fr) auto; gap: 10px; align-items: center; border: 1px solid #e0e0e0; border-radius: 6px; padding: 8px 10px; margin-bottom: 6px; background: #fff; }
.fs-row.same { border-color: #43a047; background: #f1f8f1; }
.fs-row.dup { border: 1px dashed #43a047; }
.fs-n { font-weight: 600; color: #888; font-size: 12px; }
.fs-nm { font-weight: 600; font-size: 13px; }
.fs-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.fs-bar { display: inline-block; height: 8px; border-radius: 4px; background: #1976d2; vertical-align: middle; margin-right: 8px; }
.fs-opt { flex: 1; min-width: 240px; border: 1px solid #e0e0e0; border-radius: 6px; padding: 14px; background: #fff; font-size: 13px; }
.fs-opt.rec { border: 2px solid #1976d2; }
.fs-opt ul { padding-left: 18px; }
</style>
